import { type ReactNode, useEffect, useState } from 'react';
import loadable from 'sku/@loadable/component';

import type { Environment } from 'src/app/shared/config/environments';
import { getFeatureDirectly } from 'src/app/shared/featureToggle/utils/getFeatureDirectly';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { isServerRendering } from 'src/app/utils/environment';
import { addFeatureFlag } from 'src/app/utils/logging';
import type { CatalogueProduct } from 'src/graphql/generated';
import type { HirerAccount } from 'src/types/hirerAccount';
import type { FlowType } from 'src/types/jobFlow';

import { featureConfigs } from '../config';
import { FEATURE_CART_AND_PAY_AND_POST_V2 } from '../constants';
import { isOverridesAllowed } from '../controller/utils';
import type { Feature } from '../types';
import {
  getLocalStorageFeatureState,
  setLocalStorageFeatureState,
} from '../utils/localStorageFeatureState';

import { FeatureToggleContext } from './FeatureToggleContext';

const FeatureToggleController = loadable(
  () =>
    import('src/app/shared/featureToggle/controller').then((module) => ({
      default: module.FeatureToggleController,
    })),
  { ssr: false },
);

interface ProviderProps {
  children: ReactNode;
  advertiserId: string;
  env: Environment;
  flowType?: FlowType;
  hirerAccount: HirerAccount;
  productFeatures?: CatalogueProduct['features'];
  currentPage: string;
}

export const FeatureToggleProvider = ({
  children,
  ...input
}: ProviderProps) => {
  // Feature flags can be turned on/off in local storage (for dev/testing)
  const [overrides, setOverrides] = useState<Feature[]>(
    getLocalStorageFeatureState(),
  );

  // Listen to changes to update local storage with the latest value
  useEffect(() => setLocalStorageFeatureState(overrides), [overrides]);

  const { cartFeatures } = useShoppingCart();

  const isCartEnabled = getFeatureDirectly(
    overrides,
    featureConfigs,
    input,
    FEATURE_CART_AND_PAY_AND_POST_V2,
  );

  const shouldUseFeaturesFromCart =
    isCartEnabled && input.flowType === 'create';

  const productFeatures = shouldUseFeaturesFromCart
    ? cartFeatures
    : input.productFeatures; // product features from Redux

  // Calculate the value of the features based on configuration
  const features: Feature[] = featureConfigs.map((config) =>
    config.toFeature({ ...input, productFeatures }),
  );

  features.forEach(addFeatureFlag);

  return (
    <FeatureToggleContext.Provider
      value={{
        features,
        overrides: isOverridesAllowed() ? overrides : [],
        setOverrides,
      }}
    >
      {children}
      {!isServerRendering() && isOverridesAllowed() && (
        <FeatureToggleController />
      )}
    </FeatureToggleContext.Provider>
  );
};
